import React, { useState } from 'react';
import bgImage from '../../../assets/images/bg_img.svg';
import aboutImage from '../../../assets/images/inner_design_heading.svg';
import { FaPhoneAlt, FaEnvelope, FaMapMarkerAlt } from 'react-icons/fa';
import successImg from '../../../assets/images/success_icon.svg';
import { BecomePartnerAndContactService } from '../../../services/BecomePartnerAndContactService';
import { handleCall, openGoogleMap, sendMail } from '../../../utils/HelperFunctions';
import { STATUS_SUCCESS } from '../../../utils/constants';
import ContactUsPageHelmet from '../../HelmetComponents/ContactUsPageHelmet';

type ContactFormData = {
  name: string;
  company: string;
  phone: string;
  email: string;
  message: string;
};

type ContactErrors = Partial<Record<keyof ContactFormData, string>>;

const ContactUs: React.FC = () => {
  const [formData, setFormData] = useState<ContactFormData>({
    name: '',
    company: '',
    phone: '',
    email: '',
    message: '',
  });

  const [errors, setErrors] = useState<ContactErrors>({});
  const [loading, setLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validate = (): boolean => {
    const newErrors: ContactErrors = {};
    /** Name required */
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    /**Phone required   */
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone is required';
    } else if (!/^\d+$/.test(formData.phone)) {
      newErrors.phone = 'Mobile number must be a number';
    } else if (formData.phone.length !== 10) {
      newErrors.phone = 'Phone must be exactly 10 digits';
    } else if (!/^[6-9]\d{9}$/.test(formData.phone)) {
      newErrors.phone = 'Enter a valid 10-digit Indian mobile number';
    }

    /** Email required and must be valid format */
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    /** Company required */
    if (!formData.company.trim()) {
      newErrors.company = 'Company is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      const payload = {
        fullname: formData.name,
        company: formData.company,
        phone: formData.phone,
        email: formData.email,
        comment: formData.message,
        inquiry_type: 'contact',
        website_name: null,
        business_name: null,
        current_business_type: null,
        how_long_have_you_been_oprating_your_business: null,
        do_you_currently_have_your_own_vehicle_for_distribution: null,
        preferred_area_of_distribution: null,
        business_address: null,
      };
      // console.log(payload)
      const response = await BecomePartnerAndContactService.BecomePartnerAndContactSendData(payload);
      const apiResp = response.message;
      // console.log('API response', apiResp)
      if (apiResp.status === STATUS_SUCCESS) {
        setFormData({
          name: '',
          company: '',
          phone: '',
          email: '',
          message: '',
        });
        setLoading(false);
        setShowSuccessModal(true);
      } else {
        setLoading(false);
      }
    } catch (error: any) {
      setLoading(false);
    }
  };

  return (
    <>
      <ContactUsPageHelmet />
      <section className='w-full'>
        <div
          className='relative flex flex-col items-center justify-center h-[300px] sm:h-[300px] md:h-[600px] text-center'
          style={{
            backgroundImage: `url(${bgImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <h1 className='text-[#4A2D1A] font-rubik text-[28px] sm:text-[28px] md:text-[32px] lg:text-[32px] font-bold relative md:mt-4 lg:mt-4 drop-shadow-[0_2px_2px_rgba(0,0,0,0.7)]'>
            Contact Us
          </h1>
          <div className='flex justify-center mt-4 w-full px-4'>
            <img src={aboutImage} alt='Contact Us Back Icon' className='max-w-full h-auto' />
          </div>
        </div>

        {/* --- ContactUs */}
        <section className='py-2 sm:py-2 md:py-14 lg:py-14 px-4 sm:px-8 md:px-16 bg-white'>
          <div className='max-w-6xl mx-auto bg-white bg-opacity-90 rounded-lg'>
            {/* <h2 className="font-rubik text-[28px] sm:text-[28px] md:text-[32px] lg:text-[32px] font-bold text-[#4A2D1A] text-center mb-3 sm:mb-3 md:mb-8 lg:md-8 ">
            CONTACT US
          </h2> */}

            <div className='flex flex-col md:flex-row gap-4 sm:gap-4 md:gap-10 lg:gap-10'>
              {/* Contact Form - Appears FIRST on Mobile, SECOND on Desktop */}
              <div className='md:w-1/2 order-1 md:order-2'>
                <form
                  onSubmit={handleSubmit}
                  className='space-y-3 sm:space-y-3 md:space-y-4 lg:space-y-4 font-poppins text-black text-base'
                >
                  {/* Name */}
                  <div>
                    <label className='block mb-1'>
                      Name<span className='text-red-600'>*</span>
                    </label>
                    <input
                      type='text'
                      name='name'
                      value={formData.name}
                      onChange={handleChange}
                      className={`w-full border border-[#c6c0c0] rounded-lg px-4 py-1 sm:py-1 md:py-2 lg:py-2 ${errors.name ? 'border-red-500' : ''}`}
                    />
                    {errors.name && <p className='text-red-500 text-sm'>{errors.name}</p>}
                  </div>

                  {/* Company */}
                  <div>
                    <label className='block mb-1'>
                      Company<span className='text-red-600'>*</span>
                    </label>
                    <input
                      type='text'
                      name='company'
                      value={formData.company}
                      onChange={handleChange}
                      className={`w-full border border-[#c6c0c0] rounded-lg px-4 py-1 sm:py-1 md:py-2 lg:py-2 ${errors.company ? 'border-red-500' : ''}`}
                    />
                    {errors.company && <p className='text-red-500 text-sm'>{errors.company}</p>}
                  </div>

                  {/* Phone */}
                  <div>
                    <label className='block mb-1'>
                      Phone<span className='text-red-600'>*</span>
                    </label>
                    <input
                      type='text'
                      name='phone'
                      value={formData.phone}
                      maxLength={10}
                      onChange={handleChange}
                      className={`w-full border border-[#c6c0c0] rounded-lg px-4 py-1 sm:py-1 md:py-2 lg:py-2 ${errors.phone ? 'border-red-500' : ''}`}
                    />
                    {errors.phone && <p className='text-red-500 text-sm'>{errors.phone}</p>}
                  </div>

                  {/* Email */}
                  <div>
                    <label className='block mb-1'>
                      Email<span className='text-red-600'>*</span>
                    </label>
                    <input
                      type='email'
                      name='email'
                      value={formData.email}
                      onChange={handleChange}
                      className={`w-full border border-[#c6c0c0] rounded-lg px-4 py-1 sm:py-1 md:py-2 lg:py-2 ${errors.email ? 'border-red-500' : ''}`}
                    />
                    {errors.email && <p className='text-red-500 text-sm'>{errors.email}</p>}
                  </div>

                  {/* Message */}
                  <div>
                    <label className='block mb-1'>Message</label>
                    <textarea
                      name='message'
                      rows={3}
                      value={formData.message}
                      onChange={handleChange}
                      className='w-full border border-[#c6c0c0] rounded-lg px-4 py-1 sm:py-1 md:py-2 lg:py-2'
                    />
                  </div>

                  {/* Submit Button */}
                  <div className='text-start pt-1 sm:pt-1 md:pt-4 lg:pt-4'>
                    <button
                      type='submit'
                      className='px-8 py-2 sm:py-2 md:py-3 lg:py-3 bg-red-700 text-white rounded-xl hover:bg-red-800 transition font-semibold font-rubik'
                      disabled={loading}
                    >
                      {loading ? 'Sending...' : 'Send Message'}
                    </button>
                  </div>
                </form>
              </div>

              {/* Contact Info - Appears SECOND on Mobile, FIRST on Desktop */}
              <div className='md:w-1/2 order-2 md:order-1 text-left space-y-2 sm:space-y-2 md:space-y-6 lg:space-y-6'>
                <h3 className='font-rubik text-[28px] sm:text-[28px] md:text-[32px] lg:text-[32px] font-semibold text-[#4A2D1A] text-center sm:text-center md:text-start lg:text-start'>
                  Get in Touch
                </h3>

                {/* Address */}
                <div className='flex items-start gap-6 cursor-pointer group' onClick={openGoogleMap}>
                  <FaMapMarkerAlt className='text-[#4A2D1A] mt-1 text-[32px] sm:text-[32px] md:text-[40px] lg:text-[40px] group-hover:text-[#7C0000]' />
                  <div className='cursor-pointer'>
                    <p className='font-rubik text-lg sm:text-lg md:text-2xl lg:text-2xl font-bold mb-1 sm:mb-1 md:mb-3 lg:mb-3 group-hover:text-[#7C0000]'>
                      Manufacturing Unit & Warehouse
                    </p>
                    <p className='font-poppins text-sm sm:text-sm md:text-lg lg:text-lg text-black group-hover:text-[#7C0000]'>
                      523, Near Krishna Hotel,
                      <br />
                      For Godown,
                      <br />
                      Kanera, Mehemdabad,
                      <br />
                      Kheda- 387130,
                      <br />
                      Gujarat
                    </p>
                  </div>
                </div>

                {/* Email */}
                <div className='flex items-start gap-6 cursor-pointer group' onClick={sendMail}>
                  <FaEnvelope className='text-[#4A2D1A] mt-1 text-[32px] sm:text-[32px] md:text-[40px] lg:text-[40px] group-hover:text-[#7C0000]' />
                  <div>
                    <p className='font-rubik text-lg sm:text-lg md:text-2xl lg:text-2xl font-bold mb-1 sm:mb-1 md:mb-3 lg:md-3 group-hover:text-[#7C0000]'>
                      Email Us
                    </p>
                    <p className='font-poppins text-black text-sm sm:text-sm md:text-lg lg:text-lg group-hover:text-[#7C0000]'>
                      info@globalmanikchand.in
                    </p>
                  </div>
                </div>

                {/* Phone */}
                <div className='flex items-start gap-6 cursor-pointer group' onClick={handleCall}>
                  <FaPhoneAlt className='text-[#4A2D1A] mt-1 text-[32px] sm:text-[32px] md:text-[40px] lg:text-[40px] group-hover:text-[#7C0000]' />
                  <div>
                    <p className='font-rubik text-lg sm:text-lg md:text-2xl lg:text-2xl font-bold mb-1 sm:mb-1 md:mb-3 lg:mb-3 group-hover:text-[#7C0000]'>
                      Call Us
                    </p>
                    <p className='font-poppins text-black text-sm sm:text-sm md:text-lg lg:text-lg mb-4 sm:mb-4 md:mb-0 lg:mb-0 group-hover:text-[#7C0000]'>
                      +91 91063-91065
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SUCCESS MODAL */}
        {showSuccessModal && (
          <div className='fixed inset-2 flex items-center justify-center z-50 bg-black bg-opacity-50'>
            <div className='bg-white rounded-xl shadow-xl max-w-xs sm:max-w-xs md:max-w-lg lg:max-w-lg w-full p-6 text-center '>
              <h2 className='font-rubik text-[26px] sm:text-[26px] md:text-[30px] lg:text-[30px] font-bold text-[#4A2D1A] mb-2'>
                Thank you for reaching out to Namasteji Manikchand!
              </h2>
              <img src={successImg} alt='Success' className='mx-auto size-24 sm:size-24 md:size-36 lg:size-36' />
              <p className='font-poppins text-sm sm:text-sm md:text-lg lg:text-lg text-gray-800 mb-4'>
                We have received your inquiry and one of our executives will contact you within the next 2 business days
              </p>
              <button
                onClick={() => setShowSuccessModal(false)}
                className='font-poppins bg-red-700 text-white py-2 px-6 rounded-xl hover:bg-red-800 transition font-semibold'
              >
                <strong>OK</strong>
              </button>
            </div>
          </div>
        )}

        {/* className="w-full px-4 pb-16 flex justify-center" */}
        <section className='pb-6 sm:pb-6 md:pb-14 lg:pb-14 px-4 sm:px-8 md:px-16 text-center w-full flex justify-center bg-white'>
          <div className='w-full max-w-6xl rounded-2xl overflow-hidden shadow-lg'>
            <iframe
              title='Location'
              src='https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d15062.781573804165!2d72.614556!3d22.818528!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e0!3m2!1sen!2sin!4v1693034663214!5m2!1sen!2sin'
              width='100%'
              height='450'
              style={{ border: 0 }}
              allowFullScreen
              loading='lazy'
              referrerPolicy='no-referrer-when-downgrade'
              className='rounded-2xl'
            />
          </div>
        </section>
      </section>
    </>
  );
};
export default ContactUs;
